import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Building2, CheckCircle2, Lock, FileWarning, Wrench, PoundSterling, Download } from "lucide-react";
import api, { openPdf, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtDate } from "@/lib/helpers";
import { Progress } from "@/components/ui/progress";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Panel = ({ icon: Icon, title, count, testId, children }) => (
  <div data-testid={testId} className="bg-white border border-border rounded-md">
    <div className="px-5 py-3 border-b border-border flex items-center gap-2">
      <Icon className="h-4 w-4 text-accent" /><span className="overline">{title}{count !== undefined ? ` · ${count}` : ""}</span>
    </div>
    {children}
  </div>
);

const Row = ({ onClick, testId, children }) => (
  <button type="button" data-testid={testId} onClick={onClick} className="w-full text-left px-5 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary/50 transition-colors">{children}</button>
);

const Empty = ({ text }) => <p className="px-5 py-6 text-center text-sm text-muted-foreground">{text}</p>;

function MoneyPanel({ money, nav }) {
  return (
    <Panel icon={PoundSterling} title="Money" testId="owner-money-panel">
      <div className="grid grid-cols-2 divide-x divide-border border-b border-border">
        <div className="px-5 py-4"><p className="overline text-muted-foreground">Payout owed to owner</p><p data-testid="owner-payout-owed" className="font-head text-2xl font-bold mt-1">{gbp(money.payout_owed)}</p></div>
        <div className="px-5 py-4"><p className="overline text-muted-foreground">Overdue rent</p><p data-testid="owner-overdue-rent" className={`font-head text-2xl font-bold mt-1 ${money.overdue_rent ? "text-[#9E382A]" : ""}`}>{gbp(money.overdue_rent)}</p><p className="text-xs text-muted-foreground">{money.overdue_rent_count} instalment(s)</p></div>
      </div>
      {money.unpaid_statements.length === 0 ? <Empty text="No unpaid statements." /> : money.unpaid_statements.map((s) => (
        <Row key={s.id} testId={`owner-statement-${s.id}`} onClick={() => nav(`/properties/${s.property_id}?tab=finances`)}>
          <span className="flex-1">{s.property_title} <span className="text-muted-foreground">· {fmtDate(s.period_start)} – {fmtDate(s.period_end)}</span></span>
          <span className="font-mono">{gbp(s.net_payout)}</span>
        </Row>
      ))}
    </Panel>
  );
}

function PackButton({ id, name }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await openPdf(`/landlords/${id}/pack.pdf`, true, `owner-pack-${name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return <Button variant="outline" data-testid="owner-pack-download" onClick={go} disabled={busy}><Download className="h-4 w-4 mr-1.5" />{busy ? "Preparing…" : "Download owner pack"}</Button>;
}

export default function LandlordDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/landlords/${id}`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="overline text-muted-foreground">Loading…</div>;
  const { landlord: l, properties, documents, issues, money } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button data-testid="back-button" onClick={() => nav("/landlords")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> All owners
        </button>
        {money && <PackButton id={l.id} name={l.name} />}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-border rounded-md p-6 h-fit">
          <div className="h-14 w-14 rounded-md bg-[#18181A] text-white flex items-center justify-center font-head text-xl font-bold">
            {l.name.charAt(0)}
          </div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight mt-4">{l.name}</h1>
          <div className="mt-4 space-y-2 text-sm text-muted-foreground">
            {l.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4" />{l.email}</p>}
            {l.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4" />{l.phone}</p>}
            {l.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{l.address}</p>}
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <p className="overline text-muted-foreground">Preferred channel</p>
            <p className="capitalize font-medium mt-1">{l.preferred_channel}</p>
          </div>
          {l.notes && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="overline text-muted-foreground">Notes</p>
              <p className="text-sm mt-1">{l.notes}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {money && <MoneyPanel money={money} nav={nav} />}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-accent" />
              <span className="overline">Portfolio · {properties.length}</span>
            </div>
            <div className="space-y-3">
              {properties.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground bg-white border border-border rounded-md">No properties assigned.</p>}
              {properties.map((p) => (
                <div key={p.id} data-testid={`ll-prop-${p.id}`} onClick={() => nav(`/properties/${p.id}`)} className="bg-white border border-border rounded-md p-4 flex items-center gap-4 cursor-pointer hover:border-[#C6A87C] transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.address}</p>
                  </div>
                  <div className="w-32">
                    <Progress value={p.progress.pct} className="h-1.5" />
                    <p className="text-xs font-mono text-muted-foreground mt-1 text-right">{p.progress.pct}%</p>
                  </div>
                  {p.status === "live"
                    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]"><CheckCircle2 className="h-3 w-3" />Live</span>
                    : <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground"><Lock className="h-3 w-3" />Onboarding</span>}
                </div>
              ))}
            </div>
          </div>
          {documents && (
            <div className="grid md:grid-cols-2 gap-6">
              <Panel icon={FileWarning} title="Documents expiring (60 days)" count={documents.length} testId="owner-docs-panel">
                {documents.length === 0 ? <Empty text="Nothing expiring soon." /> : documents.map((d) => (
                  <Row key={d.id} testId={`owner-doc-${d.id}`} onClick={() => nav(`/properties/${d.property_id}?tab=documents`)}>
                    <span className="flex-1 min-w-0"><span className="block truncate">{d.doc_type}</span><span className="block text-xs text-muted-foreground truncate">{d.property_title}</span></span>
                    <span className={`text-xs whitespace-nowrap ${d.expired ? "text-[#9E382A] font-semibold" : "text-muted-foreground"}`}>{d.expired ? "Expired " : ""}{fmtDate(d.expiry_date)}</span>
                  </Row>
                ))}
              </Panel>
              <Panel icon={Wrench} title="Open issues" count={issues.length} testId="owner-issues-panel">
                {issues.length === 0 ? <Empty text="No open issues." /> : issues.map((i) => (
                  <Row key={i.id} testId={`owner-issue-${i.id}`} onClick={() => nav(`/properties/${i.property_id}?tab=issues`)}>
                    <span className="flex-1 min-w-0"><span className="block truncate">{i.title}</span><span className="block text-xs text-muted-foreground truncate">{i.property_title}</span></span>
                    <span className="text-xs capitalize text-muted-foreground whitespace-nowrap">{(i.status || "").replace("_", " ")}{i.priority ? ` · ${i.priority}` : ""}</span>
                  </Row>
                ))}
              </Panel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
